import React from "react";
import {
	addTimeToDate,
	Button,
	dateTrunc,
	isSameTime,
	makeClassName,
	Row,
	Tooltip,
	useUpdateEffect,
} from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	DateTimeFieldInputProps,
	DateTimeInput,
	FormTooltipButton,
	FormType,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { AnnouncementDetailQuery } from "../../../Utilities";
import { EditSharingMenu, NestedCreateSharingMenu } from "../../Components/SharingPolicy";
import { AnnouncementFormValues, MediaAttachmentFormValues, SharingPolicyCustomFormValues } from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import styles from "./field.module.css";

/**
 * Generic props for fields of the Announcement model.
 */
type FieldProps<K extends keyof AnnouncementFormValues.Base = keyof AnnouncementFormValues.Base> = BaseFieldProps<
	Pick<AnnouncementFormValues.Base, K>
>;

/**
 * Generic props for fields of the Announcement model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof AnnouncementFormValues.Create = keyof AnnouncementFormValues.Create> =
	BaseFieldProps<Pick<AnnouncementFormValues.Create, K>>;

/**
 * Generic props for fields of the Announcement model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof AnnouncementFormValues.Detail = keyof AnnouncementFormValues.Detail> =
	BaseFieldProps<Pick<AnnouncementFormValues.Detail, K>>;

/**
 * Field component for the `title` field of the Announcement model.
 */
export function Title({ formState, ...props }: FieldProps<"title">) {
	return <TextField formState={formState} name="title" optional {...props} />;
}

/**
 * Field component for the `body` field of the Announcement model.
 */
export function Body({ formState, noHeader = true, ...props }: FieldProps<"body">) {
	return <TextField formState={formState} name="body" multiLine Input={BodyInput} noHeader={noHeader} {...props} />;
}

function BodyInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={6} {...props} />;
}

/**
 * Field component for the `startTime` field of the Announcement model.
 */
export function StartTime({ formState, label = "Post At", noOptional = true, ...props }: FieldProps<"startTime">) {
	const [min] = React.useState(() => new Date().toISOString());

	return (
		<DateTimeField
			formState={formState}
			name="startTime"
			label={label}
			optional={FormType.isCreate(formState.formType)}
			min={FormType.isCreate(formState.formType) ? min : undefined}
			noOptional={noOptional}
			precision="minute"
			Input={FormType.isCreate(formState.formType) ? StartTimeInput : undefined}
			{...props}
		/>
	);
}

function StartTimeInput({ setValue, ...props }: DateTimeFieldInputProps) {
	const [preset, setPreset] = React.useState<"now" | "tomorrow" | "custom">("now");

	useUpdateEffect(() => {
		switch (preset) {
			case "now":
				setValue(null);
				break;
			case "tomorrow":
				setValue(addTimeToDate(dateTrunc(addTimeToDate(new Date(), [1, "day"]), "day"), [6, "hours"]).toISOString());
				break;
		}
	}, [preset, setValue]);

	return (
		<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["start-time-input"]}>
			<Button variant={preset === "now" ? "primary" : "tertiary"} onClick={() => setPreset("now")}>
				Now
			</Button>
			<Button variant={preset === "tomorrow" ? "primary" : "tertiary"} onClick={() => setPreset("tomorrow")}>
				Tomorrow
			</Button>
			<FormTooltipButton formState={{} as any} autoFocus>
				{() => (
					<Tooltip isInteractable>
						<Tooltip.Body>
							<DateTimeInput setValue={setValue} {...props} style={{ width: "var(--date-time-field---width)" }} />
						</Tooltip.Body>
					</Tooltip>
				)}
				{({ onClick }) => (
					<Button
						variant={preset === "custom" ? "primary" : "tertiary"}
						onClick={() => {
							onClick();
							setPreset("custom");
						}}
					>
						Custom
					</Button>
				)}
			</FormTooltipButton>
		</Row>
	);
}

/**
 * Field component for the `endTime` field of the Announcement model.
 */
export function EndTime({ formState, label = "Expire At", ...props }: FieldProps<"endTime" | "startTime">) {
	const { startTime } = formState.formValues;

	const CreateInput = React.useCallback(
		(props: DateTimeFieldInputProps) => <EndTimeInput {...props} startTime={startTime} />,
		[startTime],
	);

	return (
		<DateTimeField
			formState={formState}
			name="endTime"
			label={label}
			precision="minute"
			Input={FormType.isCreate(formState.formType) ? CreateInput : undefined}
			{...props}
		/>
	);
}

function EndTimeInput({
	setValue,
	startTime,
	...props
}: DateTimeFieldInputProps & { startTime: AnnouncementFormValues.Base["startTime"] }) {
	const [preset, setPreset] = React.useState<"1-day" | "2-days" | "custom">("1-day");

	React.useEffect(() => {
		const resolvedStartTime = startTime ?? new Date(); // default (from server) is now
		switch (preset) {
			case "1-day":
				setValue(addTimeToDate(resolvedStartTime, [1, "day"]).toISOString());
				break;
			case "2-days":
				setValue(addTimeToDate(resolvedStartTime, [2, "days"]).toISOString());
				break;
		}
	}, [preset, startTime, setValue]);

	const startIsToday = startTime === null || isSameTime(startTime, new Date(), "day");

	return (
		<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["start-time-input"]}>
			<Button variant={preset === "1-day" ? "primary" : "tertiary"} onClick={() => setPreset("1-day")}>
				{startIsToday ? "Tomorrow" : "The Next Day"}
			</Button>
			<Button variant={preset === "2-days" ? "primary" : "tertiary"} onClick={() => setPreset("2-days")}>
				{startIsToday ? "In 2 Days" : "After 2 Days"}
			</Button>
			<FormTooltipButton formState={{} as any} autoFocus>
				{() => (
					<Tooltip isInteractable>
						<Tooltip.Body>
							<DateTimeInput setValue={setValue} {...props} style={{ width: "var(--date-time-field---width)" }} />
						</Tooltip.Body>
					</Tooltip>
				)}
				{({ onClick }) => (
					<Button
						variant={preset === "custom" ? "primary" : "tertiary"}
						onClick={() => {
							onClick();
							setPreset("custom");
						}}
					>
						Custom
					</Button>
				)}
			</FormTooltipButton>
		</Row>
	);
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<AnnouncementFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the Announcement model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the TestModel model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInAnnouncement}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInAnnouncement>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInAnnouncement formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<AnnouncementFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInAnnouncement
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace SharingPolicy {
	/**
	 * SubForm field component for the `sharingPolicy` edge of the Announcement model.
	 */
	export function SubForm({ formState, noHeader = true, className, ...props }: CreateFieldProps<"sharingPolicy">) {
		return (
			<SubFormField
				formState={formState}
				name="sharingPolicy"
				blankItem={SharingPolicyCustomFormValues.blankItemInParent}
				minItems={1}
				maxItems={1}
				formattedBlank=""
				noHeader={noHeader}
				className={makeClassName("sharing-record-sub-form-field", className)}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState }: SubFormRenderProps<SharingPolicyCustomFormValues.CreateInParent>) {
		return <NestedCreateSharingMenu formState={formState} />;
	};

	SubForm.AddButton = function (
		_: AddSubFormButtonRenderProps<
			Pick<AnnouncementFormValues.Create, "sharingPolicy">,
			SharingPolicyCustomFormValues.CreateInParent
		>,
	) {
		return <React.Fragment></React.Fragment>;
	};

	export type DetailProps = {
		sharingPolicy: AnnouncementDetailQuery["announcement"]["sharingPolicy"];
	};

	export function Detail({ sharingPolicy }: DetailProps) {
		if (!sharingPolicy) {
			return null;
		}

		return <EditSharingMenu sharingPolicy={sharingPolicy} />;
	}
}
